import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Skeleton } from './Skeleton';
import styles from './DataTable.module.css';

export interface Column<T> {
    key: string;
    label: string;
    render?: (value: unknown, row: T) => React.ReactNode;
    sortable?: boolean;
    defaultVisible?: boolean;
    locked?: boolean; // If true, column is always visible and cannot be hidden
}

export interface DataTableProps<T> {
    data: T[];
    columns: Column<T>[];
    loading?: boolean;
    tableId?: string; // Unique identifier for saving/loading configuration
    pagination?: {
        page: number;
        pageSize: number;
        /** Whether a next page exists (keyset-style; replaces total count). */
        hasMore?: boolean;
        /** @deprecated total record count is no longer computed server-side. */
        total?: number;
        onPageChange: (page: number) => void;
        onPageSizeChange: (pageSize: number) => void;
    };
    sorting?: {
        sortBy: string | null;
        sortOrder: 'asc' | 'desc' | null;
        onSort: (key: string, order: 'asc' | 'desc') => void;
    };
    onRowClick?: (row: T) => void;
    /** Floating action button that appears on row hover - better UX for copying text */
    onRowAction?: {
        label: string;
        icon?: string;
        onClick: (row: T) => void;
    };
    /** Multiple floating action buttons that appear on row hover */
    onRowActions?: Array<{
        label: string;
        icon?: React.ReactNode;
        onClick: (row: T) => void;
        variant?: 'primary' | 'secondary' | 'tertiary';
        disabled?: (row: T) => boolean;
    }>;
    emptyMessage?: string;
}

export function DataTable<T extends Record<string, any> = Record<string, unknown>>({
    data,
    columns,
    loading = false,
    tableId,
    pagination,
    sorting,
    onRowClick,
    onRowAction,
    onRowActions: onRowActionsProp,
    emptyMessage = 'No data available',
}: DataTableProps<T>) {
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    // Merge singular onRowAction into onRowActions array for backward compatibility
    const onRowActions = useMemo(() => {
        if (onRowActionsProp && onRowActionsProp.length > 0) return onRowActionsProp;
        if (onRowAction) {
            return [{
                label: onRowAction.label,
                icon: onRowAction.icon as React.ReactNode,
                onClick: onRowAction.onClick,
                variant: 'primary' as const,
            }];
        }
        return undefined;
    }, [onRowActionsProp, onRowAction]);

    const hasRowActions = !!onRowActions && onRowActions.length > 0;

    const processedColumnsRef = useRef<Set<string>>(new Set());

    const loadRawSavedConfig = (): { visibleColumns: string[]; columnOrder: string[] } | null => {
        if (!tableId) return null;
        try {
            const saved = localStorage.getItem(`table_config_${tableId}`);
            if (saved) {
                const config = JSON.parse(saved);
                return {
                    visibleColumns: config.visibleColumns || [],
                    columnOrder: config.columnOrder || [],
                };
            }
        } catch (err) {
            console.error('Failed to load saved table configuration:', err);
        }
        return null;
    };

    // Get default visible columns based on current columns prop
    const getDefaultVisibleColumns = () => {
        return new Set(columns.filter((col) => col.defaultVisible !== false).map((col) => col.key));
    };

    // Get default column order based on current columns prop
    const getDefaultColumnOrder = () => {
        return columns.map((col) => col.key);
    };

    // Get locked column keys that should always be visible
    const getLockedColumnKeys = () => {
        return new Set(columns.filter((col) => col.locked).map((col) => col.key));
    };

    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
        const lockedKeys = columns.filter((col) => col.locked).map((col) => col.key);
        const savedConfig = loadRawSavedConfig();
        if (savedConfig && savedConfig.visibleColumns.length > 0) {
            // Validate saved columns against current columns
            const validColumns = new Set(columns.map((col) => col.key));
            const savedVisible = new Set(
                savedConfig.visibleColumns.filter((key) => validColumns.has(key))
            );
            // Always include locked columns
            lockedKeys.forEach((key) => savedVisible.add(key));
            // If some valid saved columns exist, use them
            if (savedVisible.size > 0) {
                return savedVisible;
            }
        }
        return getDefaultVisibleColumns();
    });
    const [showColumnMenu, setShowColumnMenu] = useState(false);
    const [columnOrder, setColumnOrder] = useState<string[]>(() => {
        const savedConfig = loadRawSavedConfig();
        if (savedConfig && savedConfig.columnOrder.length > 0) {
            // Validate saved order against current columns
            const validColumns = new Set(columns.map((col) => col.key));
            const savedOrder = savedConfig.columnOrder.filter((key) => validColumns.has(key));
            // Add any new columns that weren't in saved config
            const newColumns = columns
                .map((col) => col.key)
                .filter((key) => !savedOrder.includes(key));
            return [...savedOrder, ...newColumns];
        }
        return getDefaultColumnOrder();
    });
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
    const [columnSearchQuery, setColumnSearchQuery] = useState('');
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

    // Effect to re-apply saved config when columns change (e.g., when dynamic columns are added after data loads)
    useEffect(() => {
        const savedConfig = loadRawSavedConfig();
        if (!savedConfig) return;

        const currentColumnKeys = new Set(columns.map((col) => col.key));

        // Find newly available columns that are in saved config but weren't processed before
        const newlyAvailableSavedColumns = savedConfig.visibleColumns.filter(
            (key) => currentColumnKeys.has(key) && !processedColumnsRef.current.has(key)
        );

        if (newlyAvailableSavedColumns.length > 0) {
            // Add newly available saved columns to visible columns
            setVisibleColumns((prev) => {
                const next = new Set(prev);
                newlyAvailableSavedColumns.forEach((key) => next.add(key));
                return next;
            });
        }

        // Update column order with any new columns from saved config that are now available
        const newlyAvailableOrderColumns = savedConfig.columnOrder.filter(
            (key) => currentColumnKeys.has(key) && !processedColumnsRef.current.has(key)
        );

        if (newlyAvailableOrderColumns.length > 0) {
            setColumnOrder((prevOrder) => {
                // Start with saved order for columns that are now available
                const existingInOrder = new Set(prevOrder);

                // Add newly available columns in their saved order position
                const newOrder = [...prevOrder];
                newlyAvailableOrderColumns.forEach((key) => {
                    if (!existingInOrder.has(key)) {
                        // Find where this column should be inserted based on saved order
                        const savedIndex = savedConfig.columnOrder.indexOf(key);
                        // Find the best insertion point
                        let insertIndex = newOrder.length;
                        for (let i = 0; i < newOrder.length; i++) {
                            const existingKeyIndex = savedConfig.columnOrder.indexOf(newOrder[i]);
                            if (existingKeyIndex > savedIndex) {
                                insertIndex = i;
                                break;
                            }
                        }
                        newOrder.splice(insertIndex, 0, key);
                    }
                });
                return newOrder;
            });
        }

        // Mark all current columns as processed
        columns.forEach((col) => processedColumnsRef.current.add(col.key));
    }, [columns, tableId]);

    // Save configuration to localStorage
    const saveConfiguration = () => {
        if (!tableId) {
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 2000);
            return;
        }

        setSaveStatus('saving');
        try {
            const config = {
                visibleColumns: Array.from(visibleColumns),
                columnOrder: columnOrder,
                savedAt: new Date().toISOString(),
            };
            localStorage.setItem(`table_config_${tableId}`, JSON.stringify(config));
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus('idle'), 2000);
        } catch (err) {
            console.error('Failed to save table configuration:', err);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 2000);
        }
    };

    const visibleColumnsList = useMemo(() => {
        const visible = columns.filter((col) => visibleColumns.has(col.key));
        // Sort by columnOrder, maintaining original order for columns not in order
        return visible.sort((a, b) => {
            const indexA = columnOrder.indexOf(a.key);
            const indexB = columnOrder.indexOf(b.key);
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    }, [columns, visibleColumns, columnOrder]);

    const toggleColumn = (key: string) => {
        // Don't allow toggling locked columns
        const column = columns.find((col) => col.key === key);
        if (column?.locked) return;

        setVisibleColumns((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
                // Add to column order if not already present
                if (!columnOrder.includes(key)) {
                    setColumnOrder((prevOrder) => [...prevOrder, key]);
                }
            }
            return next;
        });
    };

    const handleSort = (key: string) => {
        if (!sorting) return;
        const column = columns.find((col) => col.key === key);
        if (!column?.sortable) return;

        if (sorting.sortBy === key) {
            if (sorting.sortOrder === 'asc') {
                sorting.onSort(key, 'desc');
            } else {
                sorting.onSort(key, 'asc');
            }
        } else {
            sorting.onSort(key, 'asc');
        }
    };

    const getSortIcon = (key: string) => {
        if (!sorting || sorting.sortBy !== key) {
            return '↕';
        }
        return sorting.sortOrder === 'asc' ? '↑' : '↓';
    };

    const handleDragStart = (key: string) => {
        setDraggedColumn(key);
    };

    const handleDragOver = (e: React.DragEvent, key: string) => {
        e.preventDefault();
        if (draggedColumn && draggedColumn !== key) {
            setDragOverColumn(key);
        }
    };

    const handleDragLeave = () => {
        setDragOverColumn(null);
    };

    const handleDrop = (e: React.DragEvent, targetKey: string) => {
        e.preventDefault();
        if (!draggedColumn || draggedColumn === targetKey) {
            setDraggedColumn(null);
            setDragOverColumn(null);
            return;
        }

        const newOrder = [...columnOrder];
        const draggedIndex = newOrder.indexOf(draggedColumn);
        const targetIndex = newOrder.indexOf(targetKey);

        if (draggedIndex !== -1 && targetIndex !== -1) {
            // Remove dragged column from its position
            newOrder.splice(draggedIndex, 1);
            // Insert at target position
            newOrder.splice(targetIndex, 0, draggedColumn);
            setColumnOrder(newOrder);
        }

        setDraggedColumn(null);
        setDragOverColumn(null);
    };

    const handleDragEnd = () => {
        setDraggedColumn(null);
        setDragOverColumn(null);
    };

    return (
        <div className={styles.dataTableContainer}>
            <div className={styles.tableHeader}>
                {pagination && (
                    <div className={styles.paginationInfo}>
                        Showing {data.length === 0 ? 0 : ((pagination.page - 1) * pagination.pageSize) + 1} to{' '}
                        {((pagination.page - 1) * pagination.pageSize) + data.length} entries
                    </div>
                )}
                <div className={styles.tableControls}>
                    {sorting && sorting.sortBy && (
                        <button className={styles.sortButton}>
                            ↑↓ Sort {sorting.sortOrder === 'asc' ? '↑' : '↓'} 1
                        </button>
                    )}
                    {tableId && (
                        <button
                            className={styles.saveConfigButton}
                            onClick={saveConfiguration}
                            disabled={saveStatus === 'saving'}
                            title="Save current column configuration"
                        >
                            {saveStatus === 'saving'
                                ? 'Saving...'
                                : saveStatus === 'saved'
                                    ? '✓ Saved'
                                    : saveStatus === 'error'
                                        ? '✗ Error'
                                        : 'Save Configuration'}
                        </button>
                    )}
                    <div className={styles.viewMenuContainer}>
                        <button
                            className={styles.viewButton}
                            onClick={() => setShowColumnMenu(!showColumnMenu)}
                        >
                            View
                        </button>
                        {showColumnMenu && (
                            <>
                                <div
                                    className={styles.menuOverlay}
                                    onClick={() => setShowColumnMenu(false)}
                                />
                                <div className={styles.viewMenu}>
                                    <input
                                        type="text"
                                        placeholder="Search columns..."
                                        className={styles.columnSearch}
                                        value={columnSearchQuery}
                                        onChange={(e) => setColumnSearchQuery(e.target.value)}
                                    />
                                    <div className={styles.columnList}>
                                        {columns
                                            .filter((column) => {
                                                if (!columnSearchQuery.trim()) return true;
                                                const query = columnSearchQuery.toLowerCase();
                                                return (
                                                    column.label.toLowerCase().includes(query) ||
                                                    column.key.toLowerCase().includes(query)
                                                );
                                            })
                                            .map((column) => (
                                                <label
                                                    key={column.key}
                                                    className={`${styles.columnItem} ${column.locked ? styles.columnItemLocked : ''}`}
                                                    title={column.locked ? 'This column is always visible' : undefined}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={visibleColumns.has(column.key)}
                                                        onChange={() => toggleColumn(column.key)}
                                                        disabled={column.locked}
                                                    />
                                                    <span>{column.label}</span>
                                                    {column.locked && <span className={styles.lockedIcon}>🔒</span>}
                                                </label>
                                            ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            {visibleColumnsList.map((column) => (
                                <th
                                    key={column.key}
                                    className={`${styles.th} ${column.sortable ? styles.sortable : ''} ${draggedColumn === column.key ? styles.dragging : ''
                                        } ${dragOverColumn === column.key ? styles.dragOver : ''
                                        }`}
                                    draggable
                                    onDragStart={() => handleDragStart(column.key)}
                                    onDragOver={(e) => handleDragOver(e, column.key)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, column.key)}
                                    onDragEnd={handleDragEnd}
                                    onClick={(e) => {
                                        // Only sort if not dragging
                                        if (!draggedColumn && column.sortable) {
                                            handleSort(column.key);
                                        }
                                    }}
                                >
                                    <div className={styles.thContent}>
                                        <span
                                            className={styles.dragHandle}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            ⋮⋮
                                        </span>
                                        <span className={styles.columnLabel}>{column.label}</span>
                                        {column.sortable && (
                                            <span
                                                className={styles.sortIcon}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!draggedColumn) {
                                                        handleSort(column.key);
                                                    }
                                                }}
                                            >
                                                {getSortIcon(column.key)}
                                            </span>
                                        )}
                                    </div>
                                </th>
                            ))}
                            {hasRowActions && <th className={styles.thAction}></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                                    <tr key={i} className={styles.skeletonRow}>
                                        {visibleColumnsList.map((column) => (
                                            <td key={column.key} className={styles.skeletonCell}>
                                                <Skeleton
                                                    className={styles.skeletonText}
                                                    style={{
                                                        width: i % 3 === 0 ? '80%' : i % 2 === 0 ? '90%' : '100%',
                                                        height: '20px',
                                                    }}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </>
                        ) : data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={visibleColumnsList.length}
                                    className={styles.emptyCell}
                                >
                                    {emptyMessage}
                                </td>
                            </tr>
                        ) : (
                            data.map((row, index) => (
                                <tr
                                    key={index}
                                    className={`${onRowClick && !hasRowActions ? styles.clickableRow : ''} ${hasRowActions ? styles.actionRow : ''} ${hoveredRowIndex === index ? styles.hoveredRow : ''}`}
                                    onClick={() => !hasRowActions && onRowClick?.(row)}
                                    onMouseEnter={() => hasRowActions && setHoveredRowIndex(index)}
                                    onMouseLeave={() => hasRowActions && setHoveredRowIndex(null)}
                                >
                                    {visibleColumnsList.map((column) => (
                                        <td key={column.key} className={styles.td}>
                                            {column.render
                                                ? column.render(row[column.key], row)
                                                : String(row[column.key] ?? '')}
                                        </td>
                                    ))}
                                    {hasRowActions && (
                                        <td className={`${styles.actionCell} ${hoveredRowIndex === index ? styles.actionCellVisible : ''}`}>
                                            <div className={styles.actionButtonGroup}>
                                                {onRowActions!.map((action, actionIndex) => {
                                                    const isDisabled = action.disabled ? action.disabled(row) : false;
                                                    const variant = action.variant || 'primary';
                                                    return (
                                                        <button
                                                            key={actionIndex}
                                                            className={`${styles.floatingActionButton} ${variant === 'secondary' ? styles.floatingActionButtonSecondary : ''} ${variant === 'tertiary' ? styles.floatingActionButtonTertiary : ''}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (!isDisabled) {
                                                                    action.onClick(row);
                                                                }
                                                            }}
                                                            title={action.label}
                                                            disabled={isDisabled}
                                                        >
                                                            {action.icon || '→'}
                                                            <span className={styles.actionLabel}>{action.label}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {pagination && (
                <div className={styles.pagination}>
                    <button
                        className={styles.paginationButton}
                        onClick={() => pagination.onPageChange(pagination.page - 1)}
                        disabled={pagination.page === 1}
                    >
                        Previous
                    </button>
                    <span className={styles.paginationInfo}>
                        Page {pagination.page}
                    </span>
                    <button
                        className={styles.paginationButton}
                        onClick={() => pagination.onPageChange(pagination.page + 1)}
                        disabled={
                            // Prefer explicit has_more; fall back to total (old backend);
                            // finally assume "more" if a full page was returned.
                            pagination.hasMore !== undefined
                                ? !pagination.hasMore
                                : pagination.total !== undefined
                                    ? pagination.page >= Math.ceil(pagination.total / pagination.pageSize)
                                    : data.length < pagination.pageSize
                        }
                    >
                        Next
                    </button>
                    <select
                        className={styles.pageSizeSelect}
                        value={pagination.pageSize}
                        onChange={(e) => pagination.onPageSizeChange(Number(e.target.value))}
                    >
                        <option value={10}>10 per page</option>
                        <option value={25}>25 per page</option>
                        <option value={50}>50 per page</option>
                        <option value={100}>100 per page</option>
                    </select>
                </div>
            )}
        </div>
    );
}

